"use client";

import React from "react";
import { Loader2 } from "lucide-react";
import ResponsiveDialog from "@/src/shared/components/ResponsiveDialog";
import { cn } from "@/src/lib/utils";
import { useReportListing } from "../hooks/useReportListing";

/** The app's "Report this listing" row and its report screen, as a dialog. */
const ReportListing: React.FC<{ listingId: number }> = ({ listingId }) => {
  const report = useReportListing(listingId);

  return (
    <>
      <button
        type="button"
        onClick={report.open}
        className="flex items-center gap-2 text-base font-medium text-ink underline underline-offset-2"
      >
        <img
          src="/icons/listing/listing_report.svg"
          alt=""
          className="h-6 w-6"
        />
        Report this listing
      </button>

      <ResponsiveDialog
        isOpen={report.isOpen}
        onClose={report.close}
        title="Report this listing"
      >
        <div className="px-6 pb-6 pt-2">
          <h3 className="text-xl font-semibold text-ink">
            Why are you reporting this listing?
          </h3>
          <p className="mt-1 text-sm text-ink-secondary">
            This won’t be shared with the Host.
          </p>

          <div className="mt-5">
            {report.isLoadingReasons ? (
              <div className="flex justify-center py-6">
                <Loader2 className="animate-spin text-brand-600" />
              </div>
            ) : report.reasonsError ? (
              <div className="text-sm text-ink-secondary">
                Couldn&apos;t load the reasons.{" "}
                <button
                  type="button"
                  onClick={report.retryReasons}
                  className="font-semibold text-ink underline"
                >
                  Try again
                </button>
              </div>
            ) : report.reasons.length === 0 ? (
              <p className="text-sm text-ink-secondary">
                Reporting isn&apos;t available right now
              </p>
            ) : (
              <ul className="divide-y divide-[#EBEBEB]" role="radiogroup">
                {report.reasons.map((reason) => {
                  const isSelected = report.reasonId === reason.id;
                  return (
                    <li key={reason.id}>
                      <button
                        type="button"
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => report.selectReason(reason.id)}
                        className="flex w-full items-start justify-between gap-4 py-4 text-left"
                      >
                        <span>
                          <span className="block text-base text-ink">
                            {reason.title}
                          </span>
                          {reason.description && (
                            <span className="mt-0.5 block text-sm text-ink-secondary">
                              {reason.description}
                            </span>
                          )}
                        </span>
                        <span
                          className={cn(
                            "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2",
                            isSelected
                              ? "border-[#222222]"
                              : "border-[#B0B0B0]",
                          )}
                        >
                          {isSelected && (
                            <span className="h-3 w-3 rounded-full bg-[#222222]" />
                          )}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          {report.needsDetails && (
            <textarea
              value={report.details}
              onChange={(event) => report.setDetails(event.target.value)}
              placeholder="Tell us what happened"
              rows={4}
              maxLength={1000}
              className="mt-4 w-full resize-none rounded-xl border border-[#C3C6D3] p-3 text-base text-ink outline-none focus:border-[#222222]"
            />
          )}

          <button
            type="button"
            onClick={() => void report.submit()}
            disabled={!report.canSubmit || report.isSubmitting}
            className="mt-6 flex h-12 w-full items-center justify-center rounded-xl bg-[#222222] text-base font-semibold text-white transition-colors disabled:bg-[#C3C6D3]"
          >
            {report.isSubmitting ? (
              <Loader2 size={20} className="animate-spin" />
            ) : (
              "Submit"
            )}
          </button>
        </div>
      </ResponsiveDialog>
    </>
  );
};

export default ReportListing;
